// Birleşik Bayi Kaydı + Dinamik N-Adımlı Onay akışı için kod sabitleri ve
// kod -> i18n çözümleyici yardımcıları. Backend hiçbir kullanıcı metni
// döndürmez; yalnızca kod döner. Metinler FE i18n sözlüğünden üretilir.

// Talep durumu (request.status)
export const ApprovalStatus = {
    DRAFT: 0,
    PENDING: 10,
    RETURNED: 20,
    APPROVED: 30,
    REJECTED: 40
};

// Adım durumu (step.status)
export const StepStatus = {
    WAITING: 0,
    APPROVED: 1,
    REJECTED: 2,
    RETURNED: 3
};

// Değişiklik işlemi (change.operation)
export const ChangeOperation = {
    INSERT: 1,
    UPDATE: 2,
    DELETE: 3,
    CLOSE_OUT: 4
};

// Değişiklik hedef türü (change.targetType)
export const ChangeTargetType = {
    UNIFIED: 1,
    PAYMENT: 10,
    PROFIT: 11,
    AUTH_SRC_TYPE: 12,
    REGIONAL_MANAGER: 13,
    TECHNICAL_SUPPORT: 14,
    ADDRESS_REPORT: 15,
    AGREEMENT_TYPE: 16,
    ORDER_TYPE: 17,
    PRICE_LIST: 18
};

// Aksiyon günlüğü (history.action)
export const HistoryAction = {
    SEND_TO_APPROVAL: 1,
    APPROVE: 2,
    REJECT: 3,
    RETURN: 4,
    RESEND: 5
};

// Akış anahtarı (flowKey)
export const FlowKey = {
    DEALER_BASE: 'DEALER_BASE',
    DEALER_PAYMENT: 'DEALER_PAYMENT'
};

// Sekme list satır durumu (rowState) — backend staged satırları GetList'te döner
export const RowState = {
    LIVE: 0,
    PENDING_INSERT: 1,
    PENDING_UPDATE_NEW: 2,
    PENDING_UPDATE_OLD: 3,
    PENDING_DELETE: 4,
    PENDING_CLOSE_OUT: 5
};

// Satır geçmişi kaynağı (GetRowHistory.items[].source)
export const RowHistorySource = {
    LIVE: 1,
    TX_SNAPSHOT: 2,
    PENDING_SHADOW: 3
};

// Satır geçmişi durumu (GetRowHistory.items[].rowStatus)
export const RowStatus = {
    PASSIVE: 0,
    ACTIVE: 1,
    SOFT_DELETED: 2
};

/**
 * Kod -> i18n çözümleyicileri. `th` fonksiyonu useLanguage()'tan gelir.
 * Kod null/undefined ise boş string döner.
 */
export const resolveApprovalStatusText = (th, code) => (code === null || code === undefined ? '' : th(`dealerApproval.status.${code}`));

export const resolveStepStatusText = (th, code) => (code === null || code === undefined ? '' : th(`dealerApproval.stepStatus.${code}`));

export const resolveOperationText = (th, code) => (code === null || code === undefined ? '' : th(`dealerApproval.operation.${code}`));

export const resolveTargetTypeText = (th, code) => (code === null || code === undefined ? '' : th(`dealerApproval.targetType.${code}`));

export const resolveActionText = (th, code) => (code === null || code === undefined ? '' : th(`dealerApproval.action.${code}`));

export const resolveFlowText = (th, flowKey) => (!flowKey ? '' : th(`dealerApproval.flow.${flowKey}`));

export const resolveRowStateText = (th, code) => (code === null || code === undefined ? '' : th(`dealerApproval.rowState.${code}`));

export const resolveHistorySourceText = (th, code) => (code === null || code === undefined ? '' : th(`dealerApproval.history.source.${code}`));

export const resolveHistoryRowStatusText = (th, code) => (code === null || code === undefined ? '' : th(`dealerApproval.history.rowStatus.${code}`));

// Rol kodu -> i18n (backend rol string'i, örn. WarrantyManager). Çeviri yoksa
// ham kod gösterilir (th zaten NOTRANSLATION dönebildiği için fallback veriyoruz).
export const resolveRoleText = (th, roleCode) => {
    if (!roleCode) return '';
    const text = th(`dealerApproval.role.${roleCode}`, { fallback: roleCode });
    return text === 'NOTRANSLATION' ? roleCode : text;
};

// Bir alan adı -> i18n etiket (değişen alan vurgusu için). Çeviri yoksa ham alan adı.
export const resolveFieldLabel = (th, fieldName) => {
    if (!fieldName) return '';
    const text = th(`dealerRegistration.field.${fieldName}`, { fallback: fieldName });
    return text === 'NOTRANSLATION' ? fieldName : text;
};

// UNIFIED (Bayi+Adres+Marka) değişiklik kalemi alanlarının hangi bölüme ait
// olduğu. Onay Detayı ekranında değişiklikler Bayi/Adres/Marka alt-başlıklarına
// gruplanır (okunaklılık için). Alan adları camelCase beklenir.
export const UnifiedFieldSection = {
    DEALER: 'dealer',
    ADDRESS: 'address',
    BRAND: 'brand',
    OTHER: 'other'
};

const DEALER_FIELDS = new Set(['sapDealerId', 'erpDealerInteger', 'fullName', 'shortName', 'showPrice', 'showDiscount', 'showInvoices', 'canUseFullService', 'dealerIsActive']);
const ADDRESS_FIELDS = new Set(['sapAddrId', 'siteUseId', 'addressName', 'address', 'country', 'taxNo', 'taxOffice', 'addressIsActive']);
const BRAND_FIELDS = new Set(['brandId', 'modelIds', 'contractStartDate', 'contractEndDate', 'brandIsActive', 'warrantyRate', 'currency', 'profitRate']);

// camelCase alan adını UNIFIED bölümüne eşler.
export const resolveUnifiedSection = (fieldName) => {
    if (DEALER_FIELDS.has(fieldName)) return UnifiedFieldSection.DEALER;
    if (ADDRESS_FIELDS.has(fieldName)) return UnifiedFieldSection.ADDRESS;
    if (BRAND_FIELDS.has(fieldName)) return UnifiedFieldSection.BRAND;
    return UnifiedFieldSection.OTHER;
};

// PrimeReact Tag severity eşlemesi (durum rozetleri için)
export const approvalStatusSeverity = (code) => {
    switch (code) {
        case ApprovalStatus.DRAFT:
            return 'secondary';
        case ApprovalStatus.PENDING:
            return 'warning';
        case ApprovalStatus.RETURNED:
            return 'info';
        case ApprovalStatus.APPROVED:
            return 'success';
        case ApprovalStatus.REJECTED:
            return 'danger';
        default:
            return null;
    }
};

export const stepStatusSeverity = (code) => {
    switch (code) {
        case StepStatus.WAITING:
            return 'warning';
        case StepStatus.APPROVED:
            return 'success';
        case StepStatus.REJECTED:
            return 'danger';
        case StepStatus.RETURNED:
            return 'info';
        default:
            return null;
    }
};

export const operationSeverity = (code) => {
    switch (code) {
        case ChangeOperation.INSERT:
            return 'success';
        case ChangeOperation.UPDATE:
            return 'warning';
        case ChangeOperation.DELETE:
            return 'danger';
        case ChangeOperation.CLOSE_OUT:
            return 'secondary';
        default:
            return null;
    }
};

export const rowStateSeverity = (code) => {
    switch (code) {
        case RowState.LIVE:
            return 'success';
        case RowState.PENDING_INSERT:
            return 'success';
        case RowState.PENDING_UPDATE_NEW:
            return 'warning';
        case RowState.PENDING_UPDATE_OLD:
            return 'secondary';
        case RowState.PENDING_DELETE:
            return 'danger';
        case RowState.PENDING_CLOSE_OUT:
            return 'secondary';
        default:
            return null;
    }
};

// Bir kaydın düzenlenebilir olup olmadığı.
// Draft/Returned/Rejected ve Onaylandı/Aktif (mevcut) kayıtlar düzenlenebilir;
// düzenleme sonrası değişiklikler yeniden onaya gönderilir (staging).
// Reddedilen (40) kayıt da İade (20) gibi yeniden düzenlenip gönderilebilir.
// Yalnızca Pending (10) kilitlidir.
export const isEditableStatus = (overallStatus) => overallStatus === ApprovalStatus.DRAFT || overallStatus === ApprovalStatus.RETURNED || overallStatus === ApprovalStatus.REJECTED || overallStatus === ApprovalStatus.APPROVED;

// Kilitli mi (Pending iken salt-okunur).
export const isLockedStatus = (overallStatus) => overallStatus === ApprovalStatus.PENDING;

// JSON string ya da obje güvenli parse.
const safeParseJson = (json) => {
    if (!json) return null;
    if (typeof json === 'object') return json;
    try {
        return JSON.parse(json);
    } catch {
        return null;
    }
};

// Nesne anahtarlarını camelCase'e çevirir (ilk harf küçük). Backend payloadJson /
// changedFieldsJson alanları PascalCase gelebilir; FE alan adları (kolon field'ları
// ve form state'i) camelCase. Zaten camelCase ise idempotenttir (zararsız).
export const camelizeKeys = (obj) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
    const out = {};
    Object.keys(obj).forEach((k) => {
        const ck = k ? k.charAt(0).toLowerCase() + k.slice(1) : k;
        out[ck] = obj[k];
    });
    return out;
};

// tabChanges dizisini targetType -> { insertCount, updateCount, deleteCount, closeOutCount, totalCount } map'ine çevirir.
export const buildTabChangeMap = (tabChanges = []) => {
    const map = {};
    (tabChanges || []).forEach((item) => {
        if (item?.targetType == null) return;
        const insertCount = item.insertCount ?? 0;
        const updateCount = item.updateCount ?? 0;
        const deleteCount = item.deleteCount ?? 0;
        const closeOutCount = item.closeOutCount ?? 0;
        map[item.targetType] = {
            insertCount,
            updateCount,
            deleteCount,
            closeOutCount,
            totalCount: item.totalCount ?? insertCount + updateCount + deleteCount + closeOutCount
        };
    });
    return map;
};

// JSON string ya da objeyi güvenli parse edip camelCase anahtarlara çevirir (changedFieldsJson vb.).
export const parseChangedFields = (json) => camelizeKeys(safeParseJson(json));
